<!-- Expandable Gallery с навигацией - UI Component -->
<template>
    <div class="expandable-gallery-wrapper">
        <!-- Галерея -->
        <div class="expandable-gallery-container">
            <div 
                ref="galleryRef"
                class="expandable-gallery"
                :style="galleryTransform"
            >
                <div 
                    v-for="(item, index) in items" 
                    :key="index"
                    class="gallery-item"
                    :class="{ 
                        'is-hovered': hoveredIndex === index,
                        'is-last-visible': isLastVisibleItem(index)
                    }"
                    @mouseenter="hoveredIndex = index"
                    @mouseleave="hoveredIndex = null"
                    @click="handleItemClick(item, index)"
                >
                    <slot :item="item" :index="index" :is-hovered="hoveredIndex === index">
                        <!-- Fallback: просто изображение -->
                        <img 
                            v-if="item.image" 
                            :src="item.image" 
                            :alt="item.alt || `Gallery item ${index + 1}`"
                            class="gallery-image"
                        />
                    </slot>
                </div>
            </div>
        </div>

        <!-- Навигация -->
        <div class="gallery-navigation">
            <!-- Кнопка "Назад" -->
            <button 
                type="button"
                class="nav-button nav-button--prev"
                :disabled="currentIndex === 0"
                @click="goToPrev"
                aria-label="Предыдущая карточка"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M15 18L9 12L15 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>

            <!-- Индикаторы -->
            <div class="gallery-indicators">
                <button
                    v-for="position in scrollPositions"
                    :key="`indicator-${position - 1}`"
                    type="button"
                    class="indicator"
                    :class="{ 'is-active': currentIndex === position - 1 }"
                    @click="goToIndex(position - 1)"
                    :aria-label="`Перейти к позиции ${position}`"
                />
            </div>

            <!-- Кнопка "Вперёд" -->
            <button 
                type="button"
                class="nav-button nav-button--next"
                :disabled="currentIndex >= maxIndex"
                @click="goToNext"
                aria-label="Следующая карточка"
            >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 18L15 12L9 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                </svg>
            </button>
        </div>
    </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted } from 'vue'

interface GalleryItem {
    image?: string
    alt?: string
    [key: string]: any
}

interface Props {
    /** Массив элементов галереи */
    items: GalleryItem[]
    /** Количество видимых элементов одновременно (на десктопе) */
    visibleItems?: number
    /** Включить поддержку клавиатуры */
    keyboardNavigation?: boolean
}

const props = withDefaults(defineProps<Props>(), {
    visibleItems: 4,
    keyboardNavigation: true
})

// Определяем количество видимых элементов в зависимости от ширины экрана
const isMobile = ref(false)
const actualVisibleItems = computed(() => isMobile.value ? 1 : props.visibleItems)

// Проверка размера экрана
const checkScreenSize = () => {
    isMobile.value = window.innerWidth < 1024
}

// Поддержка свайпа на мобильных
const touchStartX = ref(0)
const touchEndX = ref(0)

const handleTouchStart = (e: TouchEvent) => {
    touchStartX.value = e.touches[0].clientX
}

const handleTouchEnd = (e: TouchEvent) => {
    touchEndX.value = e.changedTouches[0].clientX
    handleSwipe()
}

const handleSwipe = () => {
    const swipeThreshold = 50
    const diff = touchStartX.value - touchEndX.value
    
    if (Math.abs(diff) > swipeThreshold) {
        if (diff > 0) {
            // Свайп влево - следующий
            goToNext()
        } else {
            // Свайп вправо - предыдущий
            goToPrev()
        }
    }
}

onMounted(() => {
    checkScreenSize()
    window.addEventListener('resize', checkScreenSize)
    
    // Добавляем слушатели для свайпа
    if (galleryRef.value) {
        galleryRef.value.addEventListener('touchstart', handleTouchStart as any, { passive: true })
        galleryRef.value.addEventListener('touchend', handleTouchEnd as any, { passive: true })
    }
})

onUnmounted(() => {
    window.removeEventListener('resize', checkScreenSize)
    
    // Удаляем слушатели свайпа
    if (galleryRef.value) {
        galleryRef.value.removeEventListener('touchstart', handleTouchStart as any)
        galleryRef.value.removeEventListener('touchend', handleTouchEnd as any)
    }
})

const emit = defineEmits<{
    'item-click': [item: GalleryItem, index: number]
    'index-change': [index: number]
}>()

const hoveredIndex = ref<number | null>(null)
const currentIndex = ref(0)
const galleryRef = ref<HTMLElement | null>(null)

// Максимальный индекс для скролла
const maxIndex = computed(() => {
    return Math.max(0, props.items.length - actualVisibleItems.value)
})

// Вычисляем transform для скролла
const galleryTransform = computed(() => {
    if (currentIndex.value === 0) {
        return { transform: 'translateX(0)' }
    }
    
    const gapRem = 1
    const visibleCount = actualVisibleItems.value
    
    // Для мобильных (1 элемент): каждый шаг = 100% + gap
    if (isMobile.value) {
        const percentPerStep = 100
        const totalPercent = percentPerStep * currentIndex.value
        const totalRem = gapRem * currentIndex.value
        
        return {
            transform: `translateX(calc(-${totalPercent}% - ${totalRem}rem))`
        }
    }
    
    // Для десктопа (4 элемента)
    const widthRatio = props.items.length / visibleCount
    const percentPerStep = 100 / (widthRatio * visibleCount)
    const remPerStep = gapRem / props.items.length
    
    const totalPercent = percentPerStep * currentIndex.value
    const totalRem = remPerStep * currentIndex.value
    
    return {
        transform: `translateX(calc(-${totalPercent}% - ${totalRem}rem))`
    }
})

// Количество позиций для скролла (для индикаторов)
const scrollPositions = computed(() => {
    return maxIndex.value + 1
})

const handleItemClick = (item: GalleryItem, index: number) => {
    emit('item-click', item, index)
}

// Проверка, является ли элемент последним видимым
const isLastVisibleItem = (index: number) => {
    // Последний видимый элемент = currentIndex + visibleItems - 1
    return index === currentIndex.value + props.visibleItems - 1
}

// Навигация
const goToPrev = () => {
    if (currentIndex.value > 0) {
        currentIndex.value--
        emit('index-change', currentIndex.value)
    }
}

const goToNext = () => {
    if (currentIndex.value < maxIndex.value) {
        currentIndex.value++
        emit('index-change', currentIndex.value)
    }
}

const goToIndex = (index: number) => {
    // Ограничиваем индекс максимальным значением для скролла
    currentIndex.value = Math.min(index, maxIndex.value)
    emit('index-change', currentIndex.value)
}

// Поддержка клавиатуры
const handleKeyDown = (event: KeyboardEvent) => {
    if (!props.keyboardNavigation) return

    switch (event.key) {
        case 'ArrowLeft':
            event.preventDefault()
            goToPrev()
            break
        case 'ArrowRight':
            event.preventDefault()
            goToNext()
            break
        case 'Home':
            event.preventDefault()
            goToIndex(0)
            break
        case 'End':
            event.preventDefault()
            goToIndex(maxIndex.value)
            break
    }
}

onMounted(() => {
    if (props.keyboardNavigation) {
        window.addEventListener('keydown', handleKeyDown)
    }
})

onUnmounted(() => {
    if (props.keyboardNavigation) {
        window.removeEventListener('keydown', handleKeyDown)
    }
})
</script>

<style scoped>
.expandable-gallery-wrapper {
    position: relative;
    width: 100%;
}

.expandable-gallery-container {
    position: relative;
    width: 100%;
    overflow: hidden;
    border-radius: 30px;
}

.expandable-gallery {
    display: flex;
    gap: 1rem;
    min-height: 360px;
    transition: transform 0.7s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: translateZ(0);
    /* GPU ускорение */
}

.gallery-item {
    position: relative;
    /* Каждый элемент занимает ровно 1/4 ширины */
    flex: 0 0 calc((100% - 3rem) / 4);
    min-width: 0;
    max-width: calc((100% - 3rem) / 4);
    overflow: visible;
    cursor: pointer;
    transition: flex 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94),
                max-width 0.6s cubic-bezier(0.25, 0.46, 0.45, 0.94);
    transform: translateZ(0);
}

/* will-change только при hover для оптимизации */
.gallery-item:hover,
.gallery-item.is-hovered,
.expandable-gallery:has(.is-hovered) .gallery-item {
    will-change: flex, max-width;
}

/* При hover элемент увеличивается, остальные сжимаются */
.gallery-item.is-hovered {
    flex: 0 0 calc((100% - 3rem) * 0.45);
    max-width: calc((100% - 3rem) * 0.45);
}

/* Соседние элементы при наличии hover сжимаются */
.expandable-gallery:has(.is-hovered) .gallery-item:not(.is-hovered) {
    flex: 0 0 calc((100% - 3rem) * 0.183);
    max-width: calc((100% - 3rem) * 0.183);
}

.gallery-image {
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
}

/* Стилизация вложенных компонентов */
.gallery-item :deep(*) {
    border-radius: 30px;
}

.gallery-item :deep(article),
.gallery-item :deep(.case-card) {
    width: 100%;
    height: 100%;
}

/* Навигация */
.gallery-navigation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 1.5rem;
    margin-top: 2rem;
}

.nav-button {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 48px;
    height: 48px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.2);
    background: linear-gradient(135deg, rgba(0, 0, 0, 0.8) 0%, rgba(24, 24, 24, 0.8) 100%);
    color: #ececec;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
}

.nav-button:hover:not(:disabled) {
    border-color: rgba(255, 255, 255, 0.5);
    background: linear-gradient(135deg, rgba(24, 24, 24, 0.9) 0%, rgba(48, 48, 48, 0.9) 100%);
    transform: scale(1.05);
}

.nav-button:active:not(:disabled) {
    transform: scale(0.95);
}

.nav-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
}

.nav-button svg {
    flex-shrink: 0;
}

/* Индикаторы */
.gallery-indicators {
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.indicator {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    border: 1px solid rgba(255, 255, 255, 0.3);
    background: transparent;
    cursor: pointer;
    transition: all 0.3s ease;
    padding: 0;
}

.indicator:hover {
    border-color: rgba(255, 255, 255, 0.6);
    transform: scale(1.2);
}

.indicator.is-active {
    width: 24px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.8);
    border-color: rgba(255, 255, 255, 0.8);
}

/* Адаптивность - Мобильные устройства (горизонтальный слайдер) */
@media (max-width: 1023px) {
    .expandable-gallery-container {
        border-radius: 20px;
    }

    .expandable-gallery {
        flex-direction: row;
        gap: 1rem;
        min-height: 380px;
    }

    .gallery-item {
        /* На мобильных показываем 1 карточку на весь экран */
        flex: 0 0 100%;
        min-width: 100%;
        max-width: 100%;
        min-height: 360px;
    }

    /* Отключаем hover-эффект на мобильных */
    .gallery-item.is-hovered {
        flex: 0 0 100%;
        max-width: 100%;
    }

    .expandable-gallery:has(.is-hovered) .gallery-item:not(.is-hovered) {
        flex: 0 0 100%;
        max-width: 100%;
    }

    .gallery-navigation {
        margin-top: 1.5rem;
        gap: 1rem;
    }

    .nav-button {
        width: 48px;
        height: 48px;
    }

    .nav-button svg {
        width: 20px;
        height: 20px;
    }

    .indicator {
        width: 8px;
        height: 8px;
    }

    .indicator.is-active {
        width: 20px;
    }
}
</style>

